/**
 * @file: useLeadStats.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Aggregate counts for the summary strip. Refetches whenever the active filter
 * changes (its `keyword` included); the backend ignores the filter's status
 * field so each status tab keeps its own total even while a status is selected.
 */

import { LeadListFilter, LeadStats } from '@euler/model/lead/types';
import { useServiceFactory } from '@euler/services/factory';
import { useEffect, useMemo, useState } from 'react';

export const useLeadStats = (
  filter: LeadListFilter | undefined,
  // Bumped by the caller (the refresh control) to force a recount even when the
  // filter is unchanged — the counts move whenever anyone touches a lead.
  reloadKey?: number,
): LeadStats | undefined => {
  const { leadService } = useServiceFactory();
  const [stats, setStats] = useState<LeadStats | undefined>(undefined);

  // The endpoint computes per-status counts itself, so the status filter must
  // NOT be sent — otherwise selecting a tab would zero out the other tabs'
  // totals. Strip it (and drop the filter entirely once nothing else remains).
  const statsFilter = useMemo<LeadListFilter | undefined>(() => {
    if (!filter) return undefined;
    const rest: LeadListFilter = { ...filter };
    delete rest.status;
    return Object.keys(rest).length ? rest : undefined;
  }, [filter]);

  // The filter object is rebuilt per render; key the effect on its content so a
  // structurally-identical filter doesn't trigger a refetch.
  const filterKey = statsFilter ? JSON.stringify(statsFilter) : '';
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const next = await leadService.stats(statsFilter);
        if (!cancelled) setStats(next);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `statsFilter` is captured via its serialized key to avoid identity churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadService, filterKey, reloadKey]);
  return stats;
};
